import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import PageMeta from "@/components/PageMeta";
import SectionHeading from "@/components/SectionHeading";
import { IMAGES } from "@/lib/constants";
import {
  ArrowUpRight,
  Flame,
  ShieldCheck,
  Users,
  HeartHandshake,
  Sparkles,
  Award,
  Megaphone,
  GraduationCap,
  Building2,
  History,
  HandHeart,
  ClipboardList,
  PhoneCall,
  UsersRound,
  DoorOpen,
  Handshake,
} from "lucide-react";

const WHO_BELONGS = [
  {
    icon: ShieldCheck,
    t: "Active Members",
    b: "Detroit Fire Department members in active service — across Fire Operations, Emergency Medical Services, Fire Marshal, Fire Investigation, Training, Communications, Community Relations, and Apparatus. Active members are dues-paying constitutional members of the Phoenix under our bylaws.",
  },
  {
    icon: Award,
    t: "Retiree Members · Legacy Leaders",
    b: "Retired Detroit Fire Department members — known as our legacy leaders — continue to strengthen the Phoenix through fellowship, mentorship, and service across generations. Retiree members are also dues-paying constitutional members under our bylaws.",
  },
  {
    icon: HeartHandshake,
    t: "Supporters & Allies (Non-Member Circle)",
    b: "Community members, families, friends, businesses, and organizations who believe in Phoenix's mission and choose to walk alongside it. Supporters and allies are welcomed partners — not constitutional members under our bylaws.",
  },
  {
    icon: Sparkles,
    t: "Honorary & Legacy Connections",
    b: "Friends, partners, and long-standing supporters whose contribution to Phoenix has earned a place in the Phoenix story. Honorary and legacy connections are recognized in accordance with Phoenix bylaws but are distinct from dues-paying membership.",
  },
];

const WHY_JOIN = [
  {
    icon: Users,
    t: "Brotherhood & Sisterhood",
    b: "Belong to a community rooted in shared service, shared history, and shared purpose.",
  },
  {
    icon: Megaphone,
    t: "Advocacy & Voice",
    b: "Stand with an organization that has, since 1967, served as a voice for minorities within the Detroit Fire Department.",
  },
  {
    icon: GraduationCap,
    t: "Mentorship & Guidance",
    b: "Engage with mentors, retirees, and members who have walked the path and continue to invest in others.",
  },
  {
    icon: HandHeart,
    t: "Community Service Impact",
    b: "Take part in Phoenix outreach — from backpack giveaways to senior support — and serve Detroit alongside the Phoenix family.",
  },
  {
    icon: HeartHandshake,
    t: "Active Member Fellowship",
    b: "Active member fellowship opportunities that strengthen connection, support, and community among Detroit Fire Department members and their families.",
  },
  {
    icon: History,
    t: "Phoenix Legacy & Historic Engine 11",
    b: "Become part of a six-decade legacy of advocacy and service, with Historic Engine 11 as the gathering place and symbol of Phoenix life.",
  },
];

const PATHWAYS = [
  {
    icon: ShieldCheck,
    overline: "For Active Detroit Fire Department Members",
    t: "Active Member Inquiry",
    b: "If you are an active Detroit Fire Department member across any division — Fire Operations, Emergency Medical Services, Fire Marshal, Fire Investigation, Training, Communications, Community Relations, or Apparatus — and would like to learn more about joining the Phoenix family, please submit an inquiry. A member of Phoenix will follow up with next steps, in accordance with Phoenix bylaws and organizational procedures.",
    cta: "Active Member Inquiry",
    to: "/contact?type=membership&subject=Active%20Member%20Inquiry",
    style: "btn-gold",
  },
  {
    icon: Award,
    overline: "For Retired Detroit Fire Department Members",
    t: "Retiree Member Inquiry",
    b: "Retired Detroit Fire Department members carry the legacy of the Phoenix forward. If you would like to remain connected through retiree fellowship and Phoenix life, please submit an inquiry. A member of Phoenix will follow up in accordance with Phoenix bylaws and organizational procedures.",
    cta: "Retiree Member Inquiry",
    to: "/contact?type=membership&subject=Retiree%20Member%20Inquiry",
    style: "btn-primary",
  },
  {
    icon: HeartHandshake,
    overline: "For Community Allies, Friends & Partners",
    t: "Supporter / Ally / Community Partner Inquiry",
    b: "Community members, families, friends, businesses, and organizations are welcome to walk alongside Phoenix as supporters, allies, or community partners. This pathway routes to our general Phoenix inquiry form — supporters and allies are welcomed alongside the Phoenix family, but are not dues-paying constitutional members under our bylaws.",
    cta: "Supporter / Ally / Partner Inquiry",
    to: "/contact?type=partnership&subject=Supporter%20%2F%20Ally%20%2F%20Community%20Partner%20Inquiry",
    style: "btn-ghost",
  },
];

const STEPS = [
  {
    n: "01",
    icon: ClipboardList,
    t: "Submit an Inquiry",
    b: "Begin by submitting an inquiry through the Phoenix membership form, sharing a little about yourself and your interest.",
  },
  {
    n: "02",
    icon: PhoneCall,
    t: "Phoenix Follow-Up",
    b: "A member of Phoenix will follow up to share information and answer questions, in accordance with Phoenix bylaws and organizational procedures.",
  },
  {
    n: "03",
    icon: UsersRound,
    t: "Orientation & Connection",
    b: "Connect with the Phoenix family — meet members, learn the history, and explore the ways you can take part in Phoenix life.",
  },
  {
    n: "04",
    icon: DoorOpen,
    t: "Welcome Into Phoenix Life",
    b: "Step into the rhythm of Phoenix — fellowship, service, advocacy, mentorship, and legacy — alongside firefighters, retirees, and allies who came before.",
  },
];

const CROSS_LINKS = [
  {
    icon: Building2,
    t: "Historic Engine 11",
    b: "The gathering place and symbolic home of Phoenix life — where members, retirees, and supporters come together.",
    cta: "Explore Engine 11",
    to: "/historic-engine-11",
  },
  {
    icon: GraduationCap,
    t: "Project 100",
    b: "Phoenix's preparation and mentorship pathway for those interested in pursuing the fire service.",
    cta: "Learn About Project 100",
    to: "/programs-outreach#project-100",
  },
  {
    icon: HandHeart,
    t: "Community Outreach",
    b: "Phoenix members and supporters serve Detroit through outreach programs throughout the year.",
    cta: "Explore Outreach",
    to: "/programs-outreach",
  },
];

export default function Membership() {
  return (
    <div data-testid="membership-page">
      <PageMeta
        title="Membership"
        description="Stand with the Phoenix. Membership is open to active and retired Detroit Fire Department members across all divisions — Fire Operations, Emergency Medical Services, Fire Marshal, Fire Investigation, Training, Communications, Community Relations, and Apparatus — along with supporters and community allies."
        path="/membership"
      />
      <PageHero
        overline="Membership"
        title="Stand with the Phoenix."
        subtitle="Active Detroit Fire Department members across all divisions, retirees, supporters, and allies — there is a place for you in six decades of brotherhood, sisterhood, advocacy, and service."
        image={IMAGES.membershipHero}
      >
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
          <Link
            to="/contact?type=membership&subject=Active%20Member%20Inquiry"
            className="btn-gold w-full sm:w-auto justify-center"
            data-testid="hero-active-inquiry"
          >
            Active Member Inquiry <ArrowUpRight className="h-4 w-4" />
          </Link>
          <Link
            to="/contact?type=membership&subject=Retiree%20Member%20Inquiry"
            className="btn-primary w-full sm:w-auto justify-center"
            data-testid="hero-retiree-inquiry"
          >
            Retiree Member Inquiry
          </Link>
        </div>
      </PageHero>

      {/* ----- WELCOME / MISSION BAND ----- */}
      <section className="px-5 lg:px-8 -mt-6 lg:-mt-10" data-testid="membership-mission-band">
        <div className="max-w-7xl mx-auto rounded-2xl border border-amber-500/30 bg-amber-500/[0.06] p-6 lg:p-8 flex flex-col sm:flex-row sm:items-center gap-5">
          <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-amber-500 text-black">
            <Flame className="h-6 w-6" />
          </span>
          <p className="text-slate-100 text-base lg:text-lg leading-relaxed max-w-4xl">
            Since 1967, the Phoenix has stood for Detroit firefighters and the
            Detroit communities they serve. Membership is an invitation into a
            legacy of{" "}
            <span className="text-amber-400 italic">
              brotherhood, sisterhood, advocacy, and service
            </span>{" "}
            — and a chance to carry that legacy forward.
          </p>
        </div>
      </section>

      {/* ----- WHO BELONGS ----- */}
      <section className="py-12 lg:py-28" data-testid="membership-who">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeading
            overline="Who Belongs"
            title="The Phoenix family."
            subtitle="Phoenix membership reflects the breadth of those who have stood with the organization for nearly six decades — firefighters, retirees, and community allies."
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {WHO_BELONGS.map((w) => (
              <div
                key={w.t}
                className="card-dark p-7"
                data-testid={`who-${w.t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <w.icon className="h-7 w-7 text-amber-500" />
                <div className="font-display text-xl text-white mt-5">{w.t}</div>
                <div className="text-slate-400 mt-3 text-sm leading-relaxed">{w.b}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ----- WHY JOIN ----- */}
      <section className="py-12 lg:py-28 bg-[#0E0E0E]" data-testid="membership-why">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeading
            overline="Why Members Join the Phoenix"
            title="A legacy worth standing with."
            subtitle="Phoenix membership is about belonging and purpose — opportunities to participate in fellowship, advocacy, mentorship, and service alongside the Phoenix family."
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHY_JOIN.map((w) => (
              <div
                key={w.t}
                className="card-dark p-7 lg:p-8"
                data-testid={`why-${w.t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <w.icon className="h-7 w-7 text-amber-500" />
                <div className="font-display text-xl text-white mt-5">{w.t}</div>
                <div className="text-slate-400 mt-3 text-sm leading-relaxed">{w.b}</div>
              </div>
            ))}
          </div>
          <p className="text-slate-500 text-xs leading-relaxed mt-10 max-w-3xl">
            Specific membership participation, recognition, and engagement
            opportunities are extended in accordance with Phoenix bylaws and
            organizational procedures.
          </p>
        </div>
      </section>

      {/* ----- MEMBERSHIP PATHWAYS ----- */}
      <section className="py-12 lg:py-28" data-testid="membership-pathways">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeading
            overline="Membership Pathways"
            title="Find your place in the Phoenix."
            subtitle="Phoenix welcomes inquiries from active Detroit Fire Department members across all divisions, retired Detroit Fire Department members, and community allies who wish to support the work."
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {PATHWAYS.map((p) => (
              <div
                key={p.t}
                className="card-dark p-7 lg:p-8 flex flex-col"
                data-testid={`pathway-${p.t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <p.icon className="h-8 w-8 text-amber-500" />
                <div className="overline mt-5">{p.overline}</div>
                <div className="font-display text-2xl text-white mt-3">{p.t}</div>
                <div className="text-slate-400 mt-4 text-sm leading-relaxed flex-1">
                  {p.b}
                </div>
                <Link
                  to={p.to}
                  className={`${p.style} mt-6 w-full justify-center`}
                >
                  {p.cta} <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            ))}
          </div>
          <p className="text-slate-500 text-xs leading-relaxed mt-10 max-w-3xl">
            Phoenix does not publish specific membership dues on this page.
            Information regarding dues, meetings, onboarding, and participation
            is shared during membership follow-up, in accordance with Phoenix
            bylaws and organizational procedures.
          </p>
        </div>
      </section>

      {/* ----- HOW TO GET STARTED ----- */}
      <section className="py-12 lg:py-28 bg-[#0E0E0E]" data-testid="membership-steps">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeading
            overline="How to Get Started"
            title="From inquiry to Phoenix life."
            subtitle="The path into the Phoenix family begins with a simple inquiry and unfolds through fellowship, conversation, and welcome."
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s) => (
              <div
                key={s.n}
                className="card-dark p-7 relative"
                data-testid={`step-${s.n}`}
              >
                <div className="absolute top-5 right-5 font-display text-3xl text-amber-500/30">
                  {s.n}
                </div>
                <s.icon className="h-7 w-7 text-amber-500" />
                <div className="font-display text-lg text-white mt-5">{s.t}</div>
                <div className="text-slate-400 mt-3 text-sm leading-relaxed">{s.b}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ----- RETIREE FELLOWSHIP ----- */}
      <section className="py-12 lg:py-24" data-testid="retiree-fellowship">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="relative rounded-3xl overflow-hidden ring-ember p-8 lg:p-12">
            <img
              src={IMAGES.membershipRetireeBg}
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-35"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/40 to-red-950/35" />
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8">
                <div className="overline flex items-center gap-2">
                  <Award className="h-4 w-4 text-amber-500" /> Retiree Fellowship
                </div>
                <h3 className="font-display text-3xl lg:text-5xl text-white mt-3 leading-[1.05]">
                  Honoring those who
                  <span className="block text-amber-500">carried the Phoenix.</span>
                </h3>
                <p className="text-slate-300 mt-5 leading-relaxed max-w-2xl">
                  Retired Detroit Fire Department members, known as our legacy
                  leaders, continue to strengthen the Phoenix through fellowship,
                  mentorship, and service across generations — in accordance
                  with Phoenix bylaws and organizational procedures.
                </p>
              </div>
              <div className="lg:col-span-4 flex">
                <Link
                  to="/contact?type=membership&subject=Retiree%20Member%20Inquiry"
                  className="btn-gold w-full justify-center"
                  data-testid="retiree-fellowship-cta"
                >
                  Retiree Member Inquiry <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ----- SERVICE TO THE FIRE FAMILY (QUIET · MEMBER-FACING · DIGNIFIED) ----- */}
      <section className="pb-12 lg:pb-24" data-testid="service-fire-family">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="rounded-2xl border border-slate-800 bg-black/40 px-6 py-7 lg:px-10 lg:py-9">
            <div className="flex flex-col lg:flex-row lg:items-start gap-6">
              <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-700 text-slate-300">
                <Handshake className="h-5 w-5" />
              </span>
              <div className="flex-1">
                <div className="overline text-slate-400">
                  Service to the Fire Family
                </div>
                <p className="text-slate-300 text-base leading-relaxed mt-2 max-w-3xl">
                  Phoenix members have also stood with families and
                  communities in moments of tragedy, remembrance, and loss.
                  When called upon and welcomed by the family, this quiet act
                  of service reflects the brotherhood, sisterhood, compassion,
                  and family commitment that have long been part of Phoenix
                  life.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ----- MEMBERSHIP ACROSS THE MISSION ----- */}
      <section className="py-12 lg:py-28 bg-[#0E0E0E]" data-testid="membership-cross-links">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeading
            overline="Membership Across the Mission"
            title="One Phoenix. Many ways to belong."
            subtitle="Membership connects to every part of the Phoenix — from Historic Engine 11 to Project 100 to community outreach."
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {CROSS_LINKS.map((c) => (
              <div
                key={c.t}
                className="card-dark p-7 flex flex-col"
                data-testid={`mem-cross-${c.t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <c.icon className="h-7 w-7 text-amber-500" />
                <div className="font-display text-xl text-white mt-5">{c.t}</div>
                <div className="text-slate-400 mt-3 text-sm leading-relaxed flex-1">
                  {c.b}
                </div>
                <Link to={c.to} className="btn-ghost mt-6 w-full justify-center">
                  {c.cta} <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* (2027 bridge removed — evergreen Membership page) */}

      {/* ----- CLOSING CTA ----- */}
      <section className="py-16 lg:py-32" data-testid="membership-closing">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <div className="overline">Phoenix · Detroit · Brotherhood · Sisterhood</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white mt-3 leading-[1.05]">
            Step into the
            <span className="block text-amber-500">Phoenix family.</span>
          </h2>
          <div className="divider-gold mx-auto" />
          <p className="text-slate-400 mt-6 leading-relaxed max-w-2xl mx-auto">
            Wherever you stand — in service, in retirement, or alongside as an
            ally — there is a way to belong with the Phoenix.
          </p>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Link
              to="/contact?type=membership&subject=Active%20Member%20Inquiry"
              className="btn-gold w-full justify-center"
              data-testid="closing-active"
            >
              Active Member Inquiry
            </Link>
            <Link
              to="/contact?type=membership&subject=Retiree%20Member%20Inquiry"
              className="btn-primary w-full justify-center"
              data-testid="closing-retiree"
            >
              Retiree Member Inquiry
            </Link>
            <Link
              to="/contact?type=membership&subject=Supporter%20Inquiry"
              className="btn-ghost w-full justify-center"
              data-testid="closing-supporter"
            >
              Become a Supporter
            </Link>
            <Link
              to="/contact?type=membership"
              className="btn-ghost w-full justify-center"
              data-testid="closing-contact"
            >
              Contact Membership
            </Link>
          </div>
          <div className="mt-10 flex items-center justify-center gap-3 text-xs text-slate-500">
            <Handshake className="h-3.5 w-3.5 text-amber-500" />
            <span className="tracking-[0.28em] uppercase font-accent">
              In accordance with Phoenix bylaws and organizational procedures
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}
